<script setup lang="ts">
/**
 * Container global de toasts — teleportado para o body, fixo no topo da viewport.
 *
 * @remarks
 * Renderiza a fila singleton do composable {@link useToast}. Deve ser incluído
 * UMA vez na aplicação (montado em `App.vue`). Cada toast é centralizado, com
 * um ícone envolto por um anel SVG que se esvazia em 3s (timer visual) e um
 * botão de fechar no canto superior direito.
 *
 * @example
 * ```vue
 * <AppToast />
 * ```
 */
import { computed } from "vue";
import { useToast } from "@/shared/composables/useToast";

const { toasts, dismiss } = useToast();

const iconFor = (type: string): string =>
  type === "success"
    ? "md-checkcircle-outlined"
    : type === "error"
      ? "md-error"
      : type === "warning"
        ? "md-warningamber-outlined"
        : "md-info";

const ringDuration = (duration: number): number =>
  duration > 0 ? Math.min(duration, 3000) : 3000;
</script>

<template>
  <teleport to="body">
    <transition-group name="toast" tag="div" class="toast-stack">
      <section
        v-for="toast in toasts"
        v-bind:key="toast.id"
        class="toast"
        v-bind:class="`toast--${toast.type}`"
        role="status"
      >
        <button
          class="toast__close"
          type="button"
          aria-label="Fechar notificação"
          v-on:click="dismiss(toast.id)"
        >
          <v-icon name="md-close" />
        </button>

        <div class="toast__body">
          <div
            class="toast__icon-ring"
            v-bind:style="{
              '--toast-ring-duration': ringDuration(toast.duration) + 'ms',
            }"
          >
            <svg
              class="toast__ring"
              viewBox="0 0 40 40"
              aria-hidden="true"
            >
              <circle
                class="toast__ring-track"
                cx="20"
                cy="20"
                r="17"
              />
              <circle
                class="toast__ring-progress"
                cx="20"
                cy="20"
                r="17"
              />
            </svg>
            <v-icon
              v-bind:name="iconFor(toast.type)"
              class="toast__icon"
              aria-hidden="true"
            />
          </div>

          <p class="toast__text">{{ toast.text }}</p>
        </div>
      </section>
    </transition-group>
  </teleport>
</template>

<style lang="scss" scoped>
.toast-stack {
  position: fixed;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  align-items: center;
  pointer-events: none;
  width: min(420px, calc(100vw - var(--spacing-md) * 2));
}

.toast {
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-md) var(--spacing-lg);
  padding-right: var(--spacing-xl);
  border-radius: 0 0 var(--radius-md) var(--radius-md);
  border: 1px solid transparent;
  border-top: none;
  box-shadow: var(--shadow16), 0 8px 24px var(--colorNeutralShadowKey);
  font-size: var(--font-size-sm);
  line-height: 1.4;
  pointer-events: auto;
  background: var(--colorBackground);
  transform-origin: top center;

  &--success { border-color: var(--colorStatusSuccessStroke1); }
  &--success .toast__icon { color: var(--colorSuccess); }
  &--success .toast__ring-progress { stroke: var(--colorSuccess); }
  &--success::before { background: var(--colorSuccess); }

  &--error { border-color: var(--colorStatusDangerStroke1); }
  &--error .toast__icon { color: var(--colorError); }
  &--error .toast__ring-progress { stroke: var(--colorError); }
  &--error::before { background: var(--colorError); }

  &--info { border-color: var(--colorStatusInfoStroke1); }
  &--info .toast__icon { color: var(--colorInfo); }
  &--info .toast__ring-progress { stroke: var(--colorInfo); }
  &--info::before { background: var(--colorInfo); }

  &--warning { border-color: var(--colorStatusWarningStroke1); }
  &--warning .toast__icon { color: var(--colorWarning); }
  &--warning .toast__ring-progress { stroke: var(--colorWarning); }
  &--warning::before { background: var(--colorWarning); }

  /* "alça" — faixa colorida sob o conteúdo, accent do tipo */
  &::before {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 3px;
    border-radius: 0 0 var(--radius-md) var(--radius-md);
    opacity: 0.85;
  }
}

.toast__body {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  flex: 1;
  min-width: 0;
}

.toast__icon-ring {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
}

.toast__ring {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
}

.toast__ring-track {
  fill: none;
  stroke: var(--colorBorderSubtle);
  stroke-width: 2;
}

.toast__ring-progress {
  fill: none;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-dasharray: 106.8; /* 2 * PI * 17 */
  stroke-dashoffset: 0;
  animation: toast-ring-shrink var(--toast-ring-duration, 3000ms) linear forwards;
}

@keyframes toast-ring-shrink {
  from { stroke-dashoffset: 0; }
  to   { stroke-dashoffset: 106.8; }
}

.toast__icon {
  position: relative;
  z-index: 1;
  width: 20px;
  height: 20px;
}

.toast__text {
  flex: 1;
  color: var(--colorText);
  margin: 0;
  min-width: 0;
}

.toast__close {
  position: absolute;
  top: var(--spacing-xs);
  right: var(--spacing-xs);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: var(--colorTextTertiary);
  cursor: pointer;
  padding: 0;
  transition: background var(--transition), color var(--transition);

  .ov-icon {
    width: 14px;
    height: 14px;
  }

  &:hover {
    background: var(--colorBackgroundHover);
    color: var(--colorText);
  }

  &:focus-visible {
    outline: 2px solid var(--colorBorderFocus);
    outline-offset: 1px;
  }
}

.toast-enter-active {
  animation: toast-jelly-in 520ms cubic-bezier(0.34, 1.56, 0.64, 1) both;
}

.toast-leave-active {
  animation: toast-slide-out 280ms ease both;
  pointer-events: none;
}

@keyframes toast-jelly-in {
  0%   { transform: translateY(-100%) scaleY(0.6); opacity: 0; }
  60%  { transform: translateY(8%)   scaleY(1.08); opacity: 1; }
  78%  { transform: translateY(-4%)  scaleY(0.96); }
  90%  { transform: translateY(2%)   scaleY(1.02); }
  100% { transform: translateY(0)    scaleY(1);    opacity: 1; }
}

@keyframes toast-slide-out {
  from { transform: translateY(0) scaleY(1); opacity: 1; }
  to   { transform: translateY(-100%) scaleY(0.7); opacity: 0; }
}

@media (prefers-reduced-motion: reduce) {
  .toast-enter-active { animation: toast-slide-in-simple 220ms ease both; }
  .toast-leave-active { animation: toast-slide-out 220ms ease both; }

  @keyframes toast-slide-in-simple {
    from { transform: translateY(-100%); opacity: 0; }
    to   { transform: translateY(0);    opacity: 1; }
  }
}
</style>